import get from 'lodash/get';
import { getAsset } from 'modules/assets';
import { loadProducer, createProducer, getProducer } from 'modules/producers';
import { loadTalent, createTalent, getTalent } from 'modules/talents';
import { loadAgent, createAgent, getAgent } from 'modules/agents';
import { Account, AccountType } from './services';
import {
  fetchLoggedInAccount,
  postAccount,
  deleteAccount,
  fetchApprovalCodeValidity as apiFetchApprovalCodeValidity,
  postChangedEmail as apiChangeEmail,
  postConfirmChangedEmail as apiVerifyChangedEmail,
} from './api';
import { ACCOUNT_TYPES } from './const';
import { loadAgentAssignments } from '../agents';
import { loadManagerAssignments } from '../managers';
import {
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';

export const loadLoggedInAccount = createAsyncThunk(
  'accounts/fetchMine',
  async (_) => await fetchLoggedInAccount(),
);
export const createAccount = createAsyncThunk(
  'accounts/create',
  async ({ firstName, lastName, accountType }) =>
    await postAccount(firstName, lastName, accountType),
);
export const removeAccount = createAsyncThunk(
  'accounts/delete',
  async (accountId) => await deleteAccount(accountId),
);
export const loadAvatar = createAsyncThunk(
  'accounts/loadAvatar',
  async (profileImage) => await getAsset(profileImage),
);

export function preloadAccount() {
  return async (dispatch) => {
    const accountPayload = await dispatch(loadLoggedInAccount());
    const account = get(accountPayload, 'payload.data');

    if (!account) {
      return account;
    }

    const accountType = Account.getType(account);

    if (AccountType.isProducer(accountType)) {
      const { id: producerId } = account.producer;
      const producer = await dispatch(loadProducer(producerId));

      // TODO: load producer with account relationship
      await dispatch(loadAvatar(producer.payload.data.profileImage));
    }

    if (AccountType.isAgent(accountType)) {
      const { id: agentId } = account.agent;
      const agent = await dispatch(loadAgent(agentId));

      await dispatch(loadAvatar(agent.payload.data.profileImage));
    }

    if (AccountType.isTalent(accountType)) {
      const { id: talentId } = account.talent;
      const talent = await dispatch(loadTalent(talentId));
      dispatch(loadAgentAssignments());
      dispatch(loadManagerAssignments());

      // TODO: load talent with account relationship
      await dispatch(loadAvatar(talent.payload.data.profileImage));
    }

    return account;
  };
}

export const validateAuthCode = createAsyncThunk(
  'authCode/validate',
  async ({ authCode, data }) =>
    await apiFetchApprovalCodeValidity(authCode, data),
);

export const changeEmail = createAsyncThunk(
  'accounts/changeEmail',
  async (email) => await apiChangeEmail(email),
);

export const verifyChangedEmail = createAsyncThunk(
  'accounts/verifyChangeEmail',
  async token => await apiVerifyChangedEmail(token),
);

const accountsSlice = createSlice({
  name: 'accounts',
  initialState: {
    account: null,
    avatar: null,
    loading: false,
    accountType: null,
    errors: [],
    authCodeValid: false,
    changeEmailSuccess: false,
    changeEmailError: false,
    changedEmail: null,
    verifyChangedEmailSuccess: false,
    verifyChangedEmailError: false,
  },
  reducers: {
    clearChangeEmailStatus: (state) => {
      state.changeEmailError = false;
      state.changeEmailSuccess = false;
      state.changedEmail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadLoggedInAccount.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadLoggedInAccount.fulfilled, (state, action) => {
        const { data } = action.payload;

        state.loading = false;
        state.account = data;
        state.accountType = Account.getType(data);
      })
      .addCase(loadLoggedInAccount.rejected, (state, _) => {
        state.loading = false;
      })
      .addCase(createAccount.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(createAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.account = action.payload.data;
      })
      .addCase(createProducer.fulfilled, (state, _) => {
        state.accountType = ACCOUNT_TYPES.PRODUCER;
      })
      .addCase(createTalent.fulfilled, (state, _) => {
        state.accountType = ACCOUNT_TYPES.TALENT;
      })
      .addCase(createAgent.fulfilled, (state, _) => {
        state.accountType = ACCOUNT_TYPES.AGENT;
      })
      .addCase(loadAvatar.fulfilled, (state, action) => {
        state.avatar = action.payload;
      })
      .addCase(validateAuthCode.fulfilled, (state, _) => {
        state.authCodeValid = true;
      })
      .addCase(validateAuthCode.rejected, (state, _) => {
        state.authCodeValid = false;
      })
      .addCase(changeEmail.fulfilled, (state, action) => {
        state.changeEmailError = false;
        state.changeEmailSuccess = true;
        state.changedEmail = action.payload.data.email;
      })
      .addCase(changeEmail.rejected, (state, _) => {
        state.changeEmailError = true;
        state.changeEmailSuccess = false;
      })
      .addCase(verifyChangedEmail.fulfilled, (state, action) => {
        state.verifyChangedEmailError = false;
        state.verifyChangedEmailSuccess = true;
        state.changedEmail = action.payload.data.email;
      })
      .addCase(verifyChangedEmail.rejected, (state, _) => {
        state.verifyChangedEmailError = true;
        state.verifyChangedEmailSuccess = false;
      });
  },
  selectors: {
    getAccount: (state) => state.account,
    getAccountType: (state) => state.accountType,
    getAccountErrors: (state) => state.errors,
    getAvatar: (state) => state.avatar,
    getAuthCodeValidity: (state) => state.authCodeValid,
    getChangeEmailError: (state) => state.changeEmailError,
    getChangeEmailSuccess: (state) => state.changeEmailSuccess,
    getChangedEmail: (state) => state.changedEmail,
    getVerifyChangedEmailSuccess: (state) => state.verifyChangedEmailSuccess,
    getVerifyChangedEmailError: (state) => state.verifyChangedEmailError,
  },
});

export const reducer = accountsSlice.reducer;
export const { clearChangeEmailStatus } = accountsSlice.actions;
export const {
  getAccount,
  getAccountType,
  getAccountErrors,
  getAvatar,
  getAuthCodeValidity,
  getChangeEmailError,
  getChangeEmailSuccess,
  getChangedEmail,
  getVerifyChangedEmailError,
  getVerifyChangedEmailSuccess,
} = accountsSlice.selectors;

export const getAccountTypeLabel = createSelector(getAccount, (account) => {
  if (account) {
    return Account.getLabel(account);
  }

  return null;
});

export const getAccountByType = createSelector(
  getAccountType,
  getProducer,
  getAgent,
  getTalent,
  (accountType, producer, agent, talent) => {
    if (accountType === ACCOUNT_TYPES.PRODUCER) {
      return producer;
    }
    if (accountType === ACCOUNT_TYPES.AGENT) {
      return agent;
    }
    if (accountType === ACCOUNT_TYPES.TALENT) {
      return talent;
    }
    return null;
  },
);
export const getAccountIdByType = createSelector(
  getAccountType,
  getProducer,
  getAgent,
  getTalent,
  (accountType, producer, agent, talent) => {
    if (accountType === ACCOUNT_TYPES.PRODUCER) {
      return get(producer, 'id');
    }
    if (accountType === ACCOUNT_TYPES.AGENT) {
      return get(agent, 'id');
    }
    if (accountType === ACCOUNT_TYPES.TALENT) {
      return get(talent, 'id');
    }
    return null;
  },
);
